import { createMongoAbility } from '@casl/ability'
import { Permission, UserRole } from 'types'

export type Actions =
  | 'read'
  | 'create'
  | 'share'
  | 'favorite'
  | 'copyLink'
  | 'addToTemplates'
  | 'copy'
  | 'move'
  | 'updateMetadata'
  | 'delete'
  | 'pin'
  | 'unpin'
  | 'toggleVersionControl'
  | 'request'
  | 'export'
  | 'publish'
  | 'cleanup'
  | 'print'
  | 'toggleFocus'
  | 'edit'
  | 'requestEditAccess'
  | 'requestCommentAccess'
  | 'readHistory'
  | 'updateStatus'
  | 'readInsights'
  | 'readRecents'
  | 'readNotifications'
  | 'switchView'
  | 'rename'
  | 'requestReview'
  | 'createLayer'
  | 'restoreDocHistory'
  | 'search'

export enum SubjectType {
  ALMANAC_FILE = 'AlmanacFile',
  FOLDER = 'Folder',
  ARTICLE = 'Article',
  WORKSPACE = 'Workspace',
  DESIGN = 'Design',
}

export interface Rule {
  action: Actions
  subject: SubjectType | SubjectType[]
  conditions?: { [key: string]: any }
}

export const rules: Rule[] = [
  {
    action: 'create',
    subject: SubjectType.ALMANAC_FILE,
    conditions: { role: { $in: [UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] } },
  },
  {
    action: 'readRecents',
    subject: SubjectType.WORKSPACE,
    conditions: { role: { $in: [UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] } },
  },
  {
    action: 'readNotifications',
    subject: SubjectType.WORKSPACE,
    conditions: { role: { $in: [UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] } },
  },
  {
    action: 'share',
    subject: SubjectType.FOLDER,
    conditions: {
      role: { $in: [UserRole.GUEST, UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
      'permissions.can_edit': true,
    },
  },
  {
    action: 'favorite',
    subject: SubjectType.FOLDER,
    conditions: { role: { $in: [UserRole.GUEST, UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] } },
  },
  {
    action: 'copyLink',
    subject: SubjectType.FOLDER,
    conditions: { 'permissions.can_read': true },
  },
  {
    action: 'print',
    subject: [SubjectType.ARTICLE, SubjectType.ALMANAC_FILE],
    conditions: {
      'permissions.access': { $in: [Permission.Read, Permission.Comment, Permission.Edit, Permission.Owner] },
    },
  },
  {
    action: 'addToTemplates',
    subject: SubjectType.FOLDER,
    conditions: {
      role: { $in: [UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
      'permissions.can_edit': true,
    },
  },
  {
    action: 'copy',
    subject: SubjectType.FOLDER,
    conditions: { 'permissions.can_read': true },
  },
  {
    action: 'move',
    subject: SubjectType.FOLDER,
    conditions: {
      role: { $in: [UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
      default_project: false,
      'permissions.can_edit': true,
    },
  },
  {
    action: 'updateMetadata',
    subject: SubjectType.FOLDER,
    conditions: {
      role: { $in: [UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
      'permissions.can_edit': true,
    },
  },
  {
    action: 'rename',
    subject: SubjectType.FOLDER,
    conditions: {
      role: { $in: [UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
      'permissions.can_edit': true,
    },
  },
  {
    action: 'delete',
    subject: SubjectType.FOLDER,
    conditions: {
      role: { $in: [UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
      default_project: false,
      'permissions.can_edit': true,
    },
  },
  {
    action: 'pin',
    subject: SubjectType.FOLDER,
    conditions: {
      role: { $in: [UserRole.ADMIN, UserRole.SUPER_ADMIN] },
      isRoot: true,
      'permissions.can_read': true,
    },
  },
  {
    action: 'unpin',
    subject: SubjectType.FOLDER,
    conditions: {
      role: { $in: [UserRole.ADMIN, UserRole.SUPER_ADMIN] },
      pinned: true,
      'permissions.can_read': true,
    },
  },
  {
    action: 'toggleVersionControl',
    subject: SubjectType.FOLDER,
    conditions: {
      role: { $in: [UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
      'permissions.can_edit': true,
    },
  },
  {
    action: 'requestEditAccess',
    subject: [SubjectType.ARTICLE, SubjectType.ALMANAC_FILE],
    conditions: {
      'permissions.access': { $in: [Permission.Read, Permission.Comment] },
      role: { $in: [UserRole.GUEST, UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
    },
  },
  {
    action: 'copy',
    subject: [SubjectType.ARTICLE, SubjectType.ALMANAC_FILE, SubjectType.DESIGN],
    conditions: {
      role: { $in: [UserRole.GUEST, UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
    },
  },
  {
    action: 'copyLink',
    subject: [SubjectType.ARTICLE, SubjectType.ALMANAC_FILE, SubjectType.DESIGN],
    conditions: {
      role: { $in: [UserRole.GUEST, UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
    },
  },
  {
    action: 'favorite',
    subject: [SubjectType.ARTICLE, SubjectType.ALMANAC_FILE, SubjectType.DESIGN],
    conditions: {
      role: { $in: [UserRole.GUEST, UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
    },
  },
  {
    action: 'readInsights',
    subject: [SubjectType.ARTICLE, SubjectType.ALMANAC_FILE],
    conditions: {
      'permissions.access': { $in: [Permission.Edit, Permission.Owner] },
      role: { $in: [UserRole.GUEST, UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
    },
  },
  {
    action: 'export',
    subject: [SubjectType.ARTICLE, SubjectType.ALMANAC_FILE, SubjectType.DESIGN],
    conditions: {
      role: { $in: [UserRole.GUEST, UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
    },
  },
  {
    action: 'move',
    subject: [SubjectType.ARTICLE, SubjectType.ALMANAC_FILE, SubjectType.DESIGN],
    conditions: {
      'permissions.access': { $in: [Permission.Owner, Permission.Edit] },
      role: { $in: [UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
    },
  },
  {
    action: 'share',
    subject: [SubjectType.ARTICLE, SubjectType.ALMANAC_FILE, SubjectType.DESIGN],
    conditions: {
      role: { $in: [UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
      'permissions.access': { $in: [Permission.Edit, Permission.Owner] },
    },
  },
  {
    action: 'addToTemplates',
    subject: [SubjectType.ARTICLE, SubjectType.DESIGN],
    conditions: {
      'permissions.access': { $in: [Permission.Owner, Permission.Edit] },
      role: { $in: [UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
    },
  },
  {
    action: 'publish',
    subject: [SubjectType.ARTICLE],
    conditions: {
      'permissions.access': { $in: [Permission.Owner, Permission.Edit] },
      role: { $in: [UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
    },
  },
  {
    action: 'delete',
    subject: [SubjectType.ARTICLE, SubjectType.ALMANAC_FILE, SubjectType.DESIGN],
    conditions: {
      'permissions.access': { $in: [Permission.Owner, Permission.Edit] },
      role: { $in: [UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
    },
  },
  {
    action: 'cleanup',
    subject: [SubjectType.ARTICLE],
    conditions: {
      'permissions.access': { $in: [Permission.Owner, Permission.Edit] },
      role: { $in: [UserRole.GUEST, UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
    },
  },
  {
    action: 'toggleVersionControl',
    subject: [SubjectType.ARTICLE, SubjectType.ALMANAC_FILE],
    conditions: {
      'permissions.access': { $in: [Permission.Owner] },
      role: { $in: [UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
    },
  },
  {
    action: 'readInsights',
    subject: [SubjectType.ARTICLE],
    conditions: {
      'permissions.access': { $in: [Permission.Owner, Permission.Edit] },
      role: { $in: [UserRole.GUEST, UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
    },
  },
  {
    action: 'readHistory',
    subject: [SubjectType.ARTICLE],
    conditions: {
      'permissions.access': { $in: [Permission.Comment, Permission.Edit, Permission.Owner] },
      role: { $in: [UserRole.GUEST, UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
    },
  },
  {
    action: 'readHistory',
    subject: [SubjectType.ARTICLE],
    conditions: {
      'permissions.access': { $in: [Permission.Read] },
      role: { $in: [UserRole.GUEST, UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
      version_control_mode: true,
    },
  },
  {
    action: 'toggleFocus',
    subject: [SubjectType.ARTICLE, SubjectType.ALMANAC_FILE],
    conditions: {
      'permissions.access': { $in: [Permission.Owner, Permission.Edit] },
      role: { $in: [UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
    },
  },
  {
    action: 'updateStatus',
    subject: [SubjectType.ARTICLE, SubjectType.ALMANAC_FILE],
    conditions: {
      'permissions.access': { $in: [Permission.Owner, Permission.Edit] },
      role: { $in: [UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
    },
  },
  {
    action: 'requestCommentAccess',
    subject: [SubjectType.ARTICLE, SubjectType.ALMANAC_FILE],
    conditions: {
      'permissions.access': { $in: [Permission.Read] },
      role: { $in: [UserRole.MEMBER, UserRole.GUEST, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
    },
  },
  {
    action: 'switchView',
    subject: [SubjectType.ARTICLE, SubjectType.ALMANAC_FILE],
    conditions: {
      'permissions.access': { $in: [Permission.Comment, Permission.Edit, Permission.Owner] },
      role: { $in: [UserRole.MEMBER, UserRole.GUEST, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
    },
  },
  {
    action: 'rename',
    subject: [SubjectType.ARTICLE, SubjectType.ALMANAC_FILE, SubjectType.DESIGN],
    conditions: {
      role: { $in: [UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
      'permissions.access': { $in: [Permission.Edit, Permission.Owner] },
    },
  },
  {
    action: 'requestReview',
    subject: [SubjectType.ARTICLE, SubjectType.ALMANAC_FILE],
    conditions: {
      role: { $in: [UserRole.GUEST, UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
      'permissions.access': { $in: [Permission.Edit, Permission.Owner] },
    },
  },
  {
    action: 'createLayer',
    subject: [SubjectType.ARTICLE],
    conditions: {
      role: { $in: [UserRole.GUEST, UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
      'permissions.access': { $in: [Permission.Edit, Permission.Owner] },
      layers_enabled: true,
    },
  },
  {
    action: 'restoreDocHistory',
    subject: [SubjectType.ARTICLE],
    conditions: {
      role: { $in: [UserRole.GUEST, UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
      'permissions.access': { $in: [Permission.Edit, Permission.Owner] },
    },
  },
  {
    action: 'search',
    subject: SubjectType.WORKSPACE,
    conditions: { role: { $in: [UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] } },
  },
  {
    action: 'edit',
    subject: [SubjectType.DESIGN, SubjectType.ARTICLE, SubjectType.DESIGN],
    conditions: {
      role: { $in: [UserRole.UNAUTHED, UserRole.GUEST, UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN] },
      'permissions.access': { $in: [Permission.Edit, Permission.Owner] },
      isAssociatedAdPublished: { $ne: true },
    },
  },
]

export default createMongoAbility(rules)
